import { useParams, useNavigate } from "@tanstack/react-router";
import { customerInvoiceMissing } from "@/lib/customer-invoice";
import { CompactKpiRibbon } from "@/components/shared/CompactKpiRibbon";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Pencil,
  Loader2,
  ShoppingCart,
  FileText,
  Plus,
  Package,
  Truck,
  Trash,
  AlertCircle,
  CheckCircle2,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { trpcClient } from "@/lib/trpc";
import { formatNumber, formatMoney, formatDate } from "@/lib/utils";
import { getStatusConfig } from "@/config/status-registry";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import { InteractiveStatusSwitch } from "@/components/ui/interactive-status-switch";
import { useState, ReactNode } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Trash2 } from "lucide-react";
import {
  CustomerProductsTab,
  CustomerDeliveriesTab,
} from "./components/CustomerTabs";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import OrderListPage from "@/pages/sales/orders/OrderList";
import QuotationListPage from "@/pages/sales/quotations/QuotationList";
import { DetailField as F } from "@/components/shared/DetailField";

const fmtD = (d: string | Date | null | undefined) => formatDate(d) || "—";


export default function CustomerView({
  customerId: propId,
  isZIndex,
}: {
  customerId?: string;
  isZIndex?: boolean;
}) {
  const params = useParams({ strict: false }) as any;
  const customerId = propId || params.customerId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [subTab, setSubTab] = useState("orders");

  // Customer findOne includes: quotations (top 10), orders (top 10)
  const { data: customerData, isLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ["customer", customerId],
    queryFn: async () => trpcClient.customers.getById.query({ id: customerId }),
  });

  const c = customerData as any;

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await trpcClient.customers.delete.mutate({ id: customerId });
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });
      await queryClient.invalidateQueries({ queryKey: ["customers-lookup"] });

      navigate({ to: "/customers" });
    } catch (err: any) {
      toast.error(
        err?.message ||
          "Xóa thất bại. Khách hàng này có thể đang ràng buộc dữ liệu.",
      );
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <DetailLoadingSkeleton breadcrumbs={[{ label: "Khách hàng", to: "/customers" }]} />;
  }

  if (detailError || !c) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Khách hàng", to: "/customers" }]}
        entityLabel="Khách hàng"
        backTo="/customers"
        codeOrId={customerId}
        error={detailError}
        onRetry={() => refetchDetail()}
      />
    );
  }

  // Real schema fields for Customer
  const ordersList: any[] = c.orders || [];
  const quotationsList: any[] = c.quotations || [];
  const productsList: any[] = c.products || [];
  const deliveriesList: any[] = c.deliveries || [];

  const relatedTabs = [
    {
      id: "orders",
      label: "Đơn hàng",
      icon: <ShoppingCart className="h-4 w-4" />,
      count: c._stats?.totalOrders || ordersList.length,
      content: (
        <div className="min-h-[400px]">
          <OrderListPage embedded customerId={c.id} />
        </div>
      ),
    },
    {
      id: "quotations",
      label: "Báo giá",
      icon: <FileText className="h-4 w-4" />,
      count: quotationsList.length,
      content: (
        <div className="min-h-[400px]">
          <QuotationListPage embedded customerId={c.id} />
        </div>
      ),
    },
    {
      id: "products",
      label: "Sản phẩm",
      icon: <Package className="h-4 w-4" />,
      count: productsList.length,
      content: (
        <div className="p-3 min-h-[400px]">
          <CustomerProductsTab customerId={c.id} />
        </div>
      ),
    },
    {
      id: "deliveries",
      label: "Giao hàng",
      icon: <Truck className="h-4 w-4" />,
      count: c._stats?.totalDeliveries || deliveriesList.length,
      content: (
        <div className="p-3 min-h-[400px]">
          <CustomerDeliveriesTab customerId={c.id} />
        </div>
      ),
    },
  ];

  // Financial fields unused after layout update
  return (
    <StandardDetailLayout
        tabTitle={c.code || c.name}
      isZIndex={isZIndex}
      breadcrumbs={[{ label: "Khách hàng", to: "/customers" }]}
      title={
        <div className="flex items-center gap-2.5">
          {c.logo ? (
            <img
              src={c.logo}
              alt="Logo KH"
              className="w-7 h-7 rounded object-contain border border-slate-200 bg-white p-0.5 shrink-0"
            />
          ) : null}
          <span>{c.code}</span>
        </div>
      }
      entityId={c.id}
      entityType="customer"
      timestamps={{
        created: c.createdAt,
        updated: c.updatedAt,
        createdBy: c.salesUser?.fullName,
      }}
      status={
        <InteractiveStatusSwitch
          initialStatus={c.status || "ACTIVE"}
          activeLabel="Hoạt động"
          inactiveLabel="Ngưng"
          activeValue="ACTIVE"
          inactiveValue="INACTIVE"
          entityName="khách hàng này"
          onToggle={async (newStatus) => {
            await trpcClient.customers.update.mutate({
              id: c.id,
              data: { status: newStatus } as any,
            });
            queryClient.invalidateQueries({
              queryKey: ["customer", customerId],
            });
          }}
        />
      }
      actions={
        <div className="flex gap-2">
          {!isZIndex && (
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                navigate({
                  to: "/customers/$customerId/edit",
                  params: { customerId },
                })
              }
            >
              <Pencil size={14} className="mr-1.5" /> Sửa
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="text-rose-600 border-rose-200 hover:bg-rose-50"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Xóa
          </Button>
        </div>
      }
      showContextPanel={!isZIndex}
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Xóa khách hàng này?"
        description="Hành động này không thể hoàn tác. Sẽ xóa khách hàng cùng toàn bộ dữ liệu liên quan."
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />
      {/* ═══ 1. FLAT INFO — Layout ═══ */}
      <div className="flex flex-col xl:flex-row gap-4 mb-3">
        {/* ── LEFT: CƠ BẢN & GIAO DỊCH ── */}
        <div className="flex-1 border border-slate-200 rounded-lg px-4 py-3 bg-white">
          {/* THÔNG TIN CƠ BẢN */}
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="text-[12px] font-bold text-blue-600 uppercase tracking-wide">
              Thông tin cơ bản
            </h4>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-0 mb-3">
            <F label="Mã khách hàng" value={c.code} mono />
            <F label="Tên viết tắt" value={c.shortName} />

            <F
              label="Loại khách hàng"
              value={
                c.type === "COMPANY" ? (
                  <Badge
                    variant="outline"
                    className="font-normal text-[11px] bg-slate-100/80 text-slate-600 border-transparent shadow-none px-2 py-0"
                  >
                    Doanh nghiệp
                  </Badge>
                ) : c.type === "INDIVIDUAL" ? (
                  <Badge
                    variant="outline"
                    className="font-normal text-[11px] bg-slate-100/80 text-slate-600 border-transparent shadow-none px-2 py-0"
                  >
                    Cá nhân
                  </Badge>
                ) : (
                  c.type
                )
              }
            />
            <F
              label="Phân khúc"
              value={
                c.group ? (
                  <Badge
                    variant="outline"
                    className="font-medium text-[10px] uppercase bg-blue-600 text-white border-transparent shadow-none px-1.5 py-0"
                  >
                    {c.group}
                  </Badge>
                ) : undefined
              }
            />

            <F label="Điện thoại" value={c.phone} mono />
            <F label="Email nhận hoá đơn" value={c.email} />

            <F
              label="Mã số thuế"
              value={(() => {
                // Tình trạng đủ thông tin xuất HĐ đặt ngay cạnh MST (không thêm dòng, giữ bảng đơn hàng ở màn hình đầu)
                const missing = customerInvoiceMissing(c);
                return (
                  <span className="inline-flex flex-wrap items-baseline gap-x-2">
                    <span className="font-mono">{c.taxCode || "—"}</span>
                    <span
                      data-testid="customer-invoice-ready"
                      data-ready={missing.length === 0 ? "true" : "false"}
                      title={missing.length ? `Thiếu thông tin xuất HĐ: ${missing.join(", ")}` : "Đủ thông tin xuất hoá đơn"}
                      className={`text-[11px] font-sans font-semibold ${missing.length === 0 ? "text-emerald-700" : "text-amber-700"}`}
                    >
                      {missing.length === 0 ? "Đủ TT xuất HĐ" : `Thiếu ${missing.join(", ")}`}
                    </span>
                  </span>
                );
              })()}
              hl
            />
            <F label="Website" value={c.website} />

            <F label="Địa chỉ" value={c.address} />
            <F label="Địa chỉ giao hàng" value={c.deliveryAddress} />
            <F label="Phương thức giao hàng" value={c.deliveryMethod} />
            {(c.deliveryMethod === "Gửi nhà xe" || c.transitAddress) && (
              <F label="Kho trung chuyển" value={c.transitAddress} />
            )}
          </div>

          <div className="border-b border-slate-200/60 my-3" />

          {/* ĐẠI DIỆN & GIAO DỊCH */}
          <h4 className="text-[12px] font-bold text-blue-600 mb-1.5 uppercase tracking-wide">
            Đại diện & Giao dịch
          </h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-0">
            <F label="Người đại diện" value={c.representative} />
            <F label="Chức vụ" value={c.position} />

            <F label="Người liên hệ" value={c.contactPerson} />
            <F label="Số điện thoại liên hệ" value={c.mobile || c.phone} mono />

            <F
              label="Phương thức TT"
              value={c.paymentTerms}
              valueClass="text-amber-600"
              className="lg:col-span-2"
            />

            <F label="Dung sai màu sắc" value={c.colorTolerance} />
            <F
              label="Dung sai số lượng"
              value={
                c.tolerancePercent !== null && c.tolerancePercent !== undefined
                  ? `${c.tolerancePercent}%`
                  : "1%"
              }
            />
          </div>
        </div>

        {/* ── RIGHT: CÔNG NỢ, NGÂN HÀNG & QUẢN LÝ ── */}
        <div className="w-full xl:w-[340px] shrink-0 border border-slate-200 rounded-lg px-4 py-3 bg-white">
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="text-[12px] font-bold text-blue-600 uppercase tracking-wide">
              Công nợ & Tín dụng
            </h4>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[11px] gap-1 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 border-indigo-200"
                onClick={() => navigate({ to: `/finance/ar/ledger/${c?.id || customerId}` as any })}
                title="Mở Sổ cái chi tiết công nợ phải thu"
              >
                <BookOpen className="h-3 w-3" /> Sổ cái
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-0 mb-3">
            <F
              label="Dư nợ hiện tại"
              value={
                <button
                  type="button"
                  onClick={() => navigate({ to: "/debts", search: { customerId: c?.id || customerId } as any })}
                  className={`cursor-pointer hover:underline text-left font-bold transition-colors ${
                    Number(c.currentDebt || 0) > 0
                      ? "text-rose-600 hover:text-rose-700"
                      : "text-slate-900 hover:text-indigo-600"
                  }`}
                  title="Bấm để xem Sổ chi tiết công nợ khách hàng"
                >
                  {Number(c.currentDebt || 0).toLocaleString("vi-VN")} đ
                </button>
              }
              mono
            />
            <F
              label="Hạn mức tín dụng"
              value={c.creditLimit && Number(c.creditLimit) > 0 ? `${Number(c.creditLimit).toLocaleString("vi-VN")} đ` : "Chưa đặt"}
              mono
            />
            <F
              label="Đánh giá công nợ"
              value={
                c.creditLimit && Number(c.creditLimit) > 0 ? (
                  c.isOverLimit || Number(c.currentDebt || 0) > Number(c.creditLimit) ? (
                    <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px] gap-1 px-1.5 py-0 h-5">
                      <AlertCircle size={10} /> Vượt HM (+{formatMoney(Number(c.currentDebt) - Number(c.creditLimit))})
                    </Badge>
                  ) : Number(c.currentDebt || 0) >= Number(c.creditLimit) * 0.8 ? (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] gap-1 px-1.5 py-0 h-5">
                      Cận HM ({(Number(c.currentDebt) / Number(c.creditLimit) * 100).toFixed(0)}%)
                    </Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] gap-1 px-1.5 py-0 h-5">
                      <CheckCircle2 size={10} /> Trong hạn mức
                    </Badge>
                  )
                ) : (
                  <span className="text-slate-400 text-xs">Chưa đặt hạn mức</span>
                )
              }
            />
            <F
              label="Dư nợ đầu kỳ"
              value={
                c.initialDebt
                  ? Number(c.initialDebt).toLocaleString("vi-VN") + " đ"
                  : "0 đ"
              }
              mono
            />
            <F
              label="Hạng khách hàng"
              value={
                <Badge
                  className={
                    c.rank6m === "VIP"
                      ? "bg-amber-500 text-white border-amber-600 text-[10px]"
                      : c.rank6m === "TIER_A"
                      ? "bg-sky-500 text-white border-sky-600 text-[10px]"
                      : c.rank6m === "TIER_B"
                      ? "bg-emerald-500 text-white border-emerald-600 text-[10px]"
                      : "bg-slate-400 text-white border-slate-500 text-[10px]"
                  }
                >
                  {c.rank6m === "VIP" ? "👑 VIP" : c.rank6m === "TIER_A" ? "🥇 Hạng A" : c.rank6m === "TIER_B" ? "🥈 Hạng B" : "🥉 Hạng C"}
                </Badge>
              }
            />
          </div>

          <div className="border-b border-slate-200/60 my-3" />

          {/* NGÂN HÀNG */}
          <h4 className="text-[12px] font-bold text-blue-600 mb-1.5 uppercase tracking-wide">
            Ngân hàng
          </h4>
          <div className="flex flex-col gap-0 mb-3">
            <F label="Số tài khoản" value={c.bankAccountNumber} mono />
            <F label="Tên tài khoản" value={c.bankAccountHolder} />
            <F label="Ngân hàng" value={c.bankName} />
          </div>

          <div className="border-b border-slate-200/60 my-3" />

          {/* QUẢN LÝ */}
          <h4 className="text-[12px] font-bold text-blue-600 mb-1.5 uppercase tracking-wide">
            Quản lý
          </h4>
          <div className="flex flex-col gap-0">
            <F
              label="Nhân viên KD"
              value={
                c.salesUsers?.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {c.salesUsers.map((su: any) => (
                      <Badge
                        key={su.id}
                        variant="outline"
                        className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 px-1.5 py-0"
                      >
                        {su.user?.fullName}
                        {su.department ? ` (${su.department})` : ""}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  c.salesUser?.fullName || undefined
                )
              }
              valueClass="text-blue-600"
            />
            {c.notes && (
              <F label="Ghi chú" value={c.notes} valueClass="text-orange-600" />
            )}
          </div>
        </div>
      </div>

      {/* ═══ 2. QUICK STATS — 1 dòng gọn (30/10/2026): số đơn / giao hàng / sản phẩm đã có trên các tab bên dưới,
          chỉ giữ tiền để bảng dữ liệu không bị đẩy xuống ═══ */}
      <CompactKpiRibbon
        className="mb-2"
        testIdPrefix="customer-stats"
        items={[
          {
            key: "sales",
            label: "Doanh số",
            clickable: false,
            variant: "blue",
            count: `${formatMoney(
              c._stats?.totalSales ??
                ordersList.reduce((s: number, o: any) => s + (Number(o.totalAmount) || 0), 0),
            )} đ`,
          },
          { key: "delivered", label: "Doanh thu giao", clickable: false, variant: "blue", count: `${formatMoney(c._stats?.totalDeliveredAmount || 0)} đ` },
          { key: "debt", label: "Còn nợ", clickable: false, variant: "rose", count: `${formatMoney(c.currentDebt ?? c.initialDebt ?? 0)} đ` },
        ]}
      />

      {/* ═══ 3. FK TABS: Đơn hàng | Báo giá | Sản phẩm | Giao hàng ═══ */}
      <RelatedDataTabs tabs={relatedTabs} defaultTab="orders" />
    </StandardDetailLayout>
  );
}
